import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import {
  fetchCatalogFileList,
  createCatalogRequest,
  fetchCatalogConfig,
} from '../../../api/x_catalog'

const RESOURCE_BASE = process.env.REACT_APP_BASE_RESOURCES_API

function CatalogContents({ isMobile }) {
  const categoryList = useSelector((state) => state.category.category) || []
  const catalogCategories = useMemo(
    () => [...categoryList].sort((a, b) => (a.caSeq || 0) - (b.caSeq || 0)),
    [categoryList]
  )

  const [catalogByCategory, setCatalogByCategory] = useState({})
  const [loadingFiles, setLoadingFiles] = useState(true)

  const [brandCaSeq, setBrandCaSeq] = useState(null)
  const [companyName, setCompanyName] = useState('')
  const [requesterName, setRequesterName] = useState('')
  const [phone1, setPhone1] = useState('')
  const [phone2, setPhone2] = useState('')
  const [phone3, setPhone3] = useState('')
  const [address, setAddress] = useState('')
  const [addressDetail, setAddressDetail] = useState('')
  const [privacyAgree, setPrivacyAgree] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [submitMessage, setSubmitMessage] = useState(null)
  const [requestEnabledByCaSeq, setRequestEnabledByCaSeq] = useState({})
  const [configLoading, setConfigLoading] = useState(true)
  const [overlayDismissed, setOverlayDismissed] = useState(false)

  useEffect(() => {
    if (catalogCategories.length && brandCaSeq == null) {
      setBrandCaSeq(catalogCategories[0].caSeq)
    }
  }, [catalogCategories, brandCaSeq])

  useEffect(() => {
    let cancelled = false
    setConfigLoading(true)
    fetchCatalogConfig()
      .then((res) => {
        if (cancelled) return
        const list = res.data?.data || []
        const map = {}
        if (Array.isArray(list)) {
          list.forEach((row) => {
            if (row?.caSeq != null) {
              map[row.caSeq] = row.requestEnabled !== 'N'
            }
          })
        }
        setRequestEnabledByCaSeq(map)
      })
      .catch((e) => {
        console.error(e)
      })
      .finally(() => {
        if (!cancelled) setConfigLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const brandRequestEnabled = useMemo(() => {
    if (brandCaSeq == null) return true
    if (requestEnabledByCaSeq[brandCaSeq] === undefined) return true
    return requestEnabledByCaSeq[brandCaSeq]
  }, [brandCaSeq, requestEnabledByCaSeq])

  useEffect(() => {
    setOverlayDismissed(false)
  }, [brandCaSeq])

  const loadFiles = useCallback(async () => {
    if (!catalogCategories.length) {
      setLoadingFiles(false)
      return
    }
    setLoadingFiles(true)
    try {
      const entries = await Promise.all(
        catalogCategories.map(async (cat) => {
          const [pdfRes, thumbRes] = await Promise.all([
            fetchCatalogFileList({ fileBizSeq: cat.caSeq, code: 'CATALOG' }),
            fetchCatalogFileList({
              fileBizSeq: cat.caSeq,
              code: 'CATALOG_THUMB',
            }),
          ])
          return [
            cat.caSeq,
            {
              pdf: pdfRes.data?.data?.[0] || null,
              thumb: thumbRes.data?.data?.[0] || null,
            },
          ]
        })
      )
      const map = {}
      entries.forEach(([caSeq, catalog]) => {
        map[caSeq] = catalog
      })
      setCatalogByCategory(map)
    } catch (e) {
      console.error(e)
    } finally {
      setLoadingFiles(false)
    }
  }, [catalogCategories])

  useEffect(() => {
    loadFiles()
  }, [loadFiles])

  const categoriesWithPdf = useMemo(
    () =>
      catalogCategories.filter((cat) => catalogByCategory[cat.caSeq]?.pdf),
    [catalogCategories, catalogByCategory]
  )

  const catalogBrandLabel = useMemo(() => {
    if (loadingFiles) return ''
    return categoriesWithPdf
      .map((c) => c.caName)
      .filter(Boolean)
      .join(' / ')
  }, [categoriesWithPdf, loadingFiles])

  const openPostcode = () => {
    if (!window.daum?.Postcode) {
      window.alert('주소 검색을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.')
      return
    }
    new window.daum.Postcode({
      oncomplete: (data) => {
        setAddress(data.roadAddress || data.jibunAddress || '')
      },
    }).open()
  }

  const handleSubmit = async () => {
    if (!brandRequestEnabled) return
    if (!brandCaSeq) {
      window.alert('브랜드를 선택해 주세요.')
      return
    }
    if (!companyName.trim() || !requesterName.trim()) {
      window.alert('업체명과 이름을 입력해 주세요.')
      return
    }
    const phone = [phone1, phone2, phone3].filter(Boolean).join('-')
    if (!phone || phone.length < 10) {
      window.alert('연락처를 입력해 주세요.')
      return
    }
    if (!address.trim()) {
      window.alert('주소를 입력해 주세요.')
      return
    }
    if (!privacyAgree) {
      window.alert('개인정보 이용약관에 동의해 주세요.')
      return
    }

    setSubmitting(true)
    setSubmitMessage(null)
    try {
      const res = await createCatalogRequest({
        caSeq: brandCaSeq,
        companyName: companyName.trim(),
        requesterName: requesterName.trim(),
        phone,
        address: address.trim(),
        addressDetail: addressDetail.trim(),
        privacyAgreeYn: 'Y',
      })
      if (res.data?.result === 'succ') {
        setSubmitMessage({ type: 'success', text: '카탈로그 신청이 완료되었습니다.' })
        setCompanyName('')
        setRequesterName('')
        setPhone1('')
        setPhone2('')
        setPhone3('')
        setAddress('')
        setAddressDetail('')
        setPrivacyAgree(false)
      } else {
        setSubmitMessage({
          type: 'error',
          text: res.data?.message || '신청에 실패했습니다.',
        })
      }
    } catch (e) {
      setSubmitMessage({ type: 'error', text: '신청 중 오류가 발생했습니다.' })
    } finally {
      setSubmitting(false)
    }
  }

  const downloadUrl = (file) => `${RESOURCE_BASE}${file.filePath}`

  return (
    <PageWrap $isMobile={isMobile}>
      <Section $isMobile={isMobile}>
        <SectionContent $isMobile={isMobile}>
          <SectionTitle $isMobile={isMobile}>
            전자 카탈로그 다운로드
          </SectionTitle>
          <SectionDesc $isMobile={isMobile}>
            원하시는 카탈로그를 PDF로 다운 받아 편리하게 확인하세요.
          </SectionDesc>

        {loadingFiles ? (
            <EmptyHint>카탈로그를 불러오는 중입니다.</EmptyHint>
        ) : (
            <CardRow $isMobile={isMobile}>
            {categoriesWithPdf.map((cat) => {
              const { pdf, thumb } = catalogByCategory[cat.caSeq] || {}
              return (
                <CatalogCard key={cat.caSeq} $isMobile={isMobile}>
                  <CategoryLabel $isMobile={isMobile}>{cat.caName}</CategoryLabel>
                  {thumb ? (
                    <CardThumbImg
                      $isMobile={isMobile}
                      src={downloadUrl(thumb)}
                      alt={thumb.fileOriginalName || 'catalog thumbnail'}
                    />
                  ) : (
                    <CardThumbBox $isMobile={isMobile}>
                      <ThumbTitle>
                        {pdf.fileInfo || pdf.fileOriginalName}
                      </ThumbTitle>
                    </CardThumbBox>
                  )}
                  <DownloadBtn
                    href={downloadUrl(pdf)}
                    target="_blank"
                    rel="noreferrer"
                    download
                  >
                    PDF 다운로드
                    <DownloadIcon viewBox="0 0 24 24" aria-hidden>
                      <path
                        fill="currentColor"
                        d="M12 3v10.55l3.3-3.3 1.4 1.42L12 17.4l-4.7-4.73 1.42-1.42 3.28 3.3V3h2zm-8 14h16v2H4v-2z"
                      />
                    </DownloadIcon>
                  </DownloadBtn>
                </CatalogCard>
              )
            })}
            </CardRow>
        )}
        </SectionContent>
      </Section>

      <Section $isMobile={isMobile} $form>
        <SectionContent $isMobile={isMobile}>
          <SectionTitle $isMobile={isMobile}>
            카탈로그 신청
          </SectionTitle>
          <SectionDesc $isMobile={isMobile}>
            인쇄용 카탈로그를 원하시는 고객께서는 아래 정보 입력 후 신청해주세요.
            (1인1권 제공)
          </SectionDesc>

          <FormWrap $isMobile={isMobile}>
          <FieldRow $isMobile={isMobile} $brand>
            <FieldLabel $isMobile={isMobile}>브랜드</FieldLabel>
            <BrandRow $isMobile={isMobile}>
              {catalogCategories.map((cat) => {
                const catEnabled =
                  requestEnabledByCaSeq[cat.caSeq] !== false
                return (
                  <BrandBtn
                    key={cat.caSeq}
                    type="button"
                    $active={brandCaSeq === cat.caSeq}
                    $paused={!catEnabled}
                    onClick={() => setBrandCaSeq(cat.caSeq)}
                  >
                    {cat.caName}
                  </BrandBtn>
                )
              })}
            </BrandRow>
          </FieldRow>

          <FormBody $disabled={!brandRequestEnabled}>
          <FieldRow $isMobile={isMobile}>
            <FieldLabel $isMobile={isMobile}>업체명</FieldLabel>
            <TextInput
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder=""
              disabled={!brandRequestEnabled}
            />
          </FieldRow>

          <FieldRow $isMobile={isMobile}>
            <FieldLabel $isMobile={isMobile}>이름</FieldLabel>
            <TextInput
              value={requesterName}
              onChange={(e) => setRequesterName(e.target.value)}
              disabled={!brandRequestEnabled}
            />
          </FieldRow>

          <FieldRow $isMobile={isMobile}>
            <FieldLabel $isMobile={isMobile}>연락처</FieldLabel>
            <PhoneRow>
              <PhoneInput
                value={phone1}
                onChange={(e) => setPhone1(e.target.value.replace(/\D/g, ''))}
                maxLength={4}
                disabled={!brandRequestEnabled}
              />
              <PhoneDash>-</PhoneDash>
              <PhoneInput
                value={phone2}
                onChange={(e) => setPhone2(e.target.value.replace(/\D/g, ''))}
                maxLength={4}
                disabled={!brandRequestEnabled}
              />
              <PhoneDash>-</PhoneDash>
              <PhoneInput
                value={phone3}
                onChange={(e) => setPhone3(e.target.value.replace(/\D/g, ''))}
                maxLength={4}
                disabled={!brandRequestEnabled}
              />
            </PhoneRow>
          </FieldRow>

          <FieldRow $isMobile={isMobile}>
            <FieldLabel $isMobile={isMobile}>주소</FieldLabel>
            <AddressBlock>
              <AddressRow>
                <TextInput
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  readOnly
                  placeholder="주소"
                  disabled={!brandRequestEnabled}
                />
                <PostcodeBtn type="button" onClick={openPostcode} disabled={!brandRequestEnabled}>
                  주소검색
                </PostcodeBtn>
              </AddressRow>
              <TextInput
                value={addressDetail}
                onChange={(e) => setAddressDetail(e.target.value)}
                placeholder="상세주소"
                disabled={!brandRequestEnabled}
              />
            </AddressBlock>
          </FieldRow>

          <PrivacyRow $isMobile={isMobile}>
            <label>
              <input
                type="checkbox"
                checked={privacyAgree}
                onChange={(e) => setPrivacyAgree(e.target.checked)}
                disabled={!brandRequestEnabled}
              />
              <span>[필수] 개인정보 이용약관 동의</span>
            </label>
            <PrivacyLink $isMobile={isMobile} href="https://www.eagon.com/?act=info.privacy" target="_blank" rel="noreferrer">
              자세히
            </PrivacyLink>
          </PrivacyRow>

          <PrivacyDesc $isMobile={isMobile}>
            고객(정보주체)의 개인정보보호 및 권리는 개인정보 보호법 및 관계 법령에
            따라 안전하게 관리하고 있습니다. 자세한 사항은 이건
            개인정보처리방침에서 확인할 수 있습니다.
          </PrivacyDesc>

          <FormDivider />

          <FormNotice $isMobile={isMobile}>
            신청하신 카탈로그는 내부 검토 후 순차적으로 발송되며, 경우에 따라
            발송이 제한될 수 있습니다.
          </FormNotice>

          <SubmitBtn
            type="button"
            onClick={handleSubmit}
            disabled={!brandRequestEnabled || submitting}
            $isMobile={isMobile}
          >
            {submitting ? '신청 중...' : '신청하기'}
          </SubmitBtn>

          {submitMessage && (
            <SubmitMsg $type={submitMessage.type}>{submitMessage.text}</SubmitMsg>
          )}

          {!configLoading && !brandRequestEnabled && !overlayDismissed && (
            <Overlay>
              <OverlayClose
                type="button"
                aria-label="닫기"
                onClick={() => setOverlayDismissed(true)}
              >
                ×
              </OverlayClose>
              <OverlayIcon $isMobile={isMobile} aria-hidden>
                <WarningTriangle viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"
                  />
                </WarningTriangle>
              </OverlayIcon>
              <OverlayText $isMobile={isMobile}>
                현재 선택하신 브랜드는 카탈로그 신청이 불가합니다.
                <br />
                빠른 시일 내 재개될 예정이오니 조금만 기다려 주세요.
              </OverlayText>
            </Overlay>
          )}
          </FormBody>
          </FormWrap>
        </SectionContent>
      </Section>
    </PageWrap>
  )
}

export default CatalogContents

const PageWrap = styled.div`
  width: 100%;
  max-width: ${(p) => (p.$isMobile ? '100%' : '1200px')};
  margin: 0 auto;
  padding: ${(p) => (p.$isMobile ? '0 20px 40px' : '0 40px 80px')};
  box-sizing: border-box;
  overflow-x: hidden;
`

const Section = styled.section`
  margin-top: ${(p) => (p.$isMobile ? '10px' : '30px')};
  margin-bottom: ${(p) => (p.$isMobile ? '48px' : '72px')};
  position: relative;
`

const SectionTitle = styled.h2`
  font-family: 'Noto Sans KR', sans-serif;
  font-weight: 500;
  font-size: ${(p) => (p.$isMobile ? '22px' : '28px')};
  color: #111;
  text-align: left;
  margin: 0 0 12px;
  letter-spacing: -0.5px;
`

const SectionDesc = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: ${(p) => (p.$isMobile ? '14px' : '16px')};
  color: #666;
  text-align: left;
  margin: 0 0 ${(p) => (p.$isMobile ? '28px' : '40px')};
  line-height: 1.5;
`

/** 신청 폼(FormWrap)과 동일한 콘텐츠 폭 (PC 720px) */
const SectionContent = styled.div`
  width: 100%;
  max-width: ${(p) => (p.$isMobile ? '100%' : '720px')};
  margin: 0 auto;
  box-sizing: border-box;
`

const CategoryLabel = styled.h3`
  font-size: ${(p) => (p.$isMobile ? '16px' : '18px')};
  font-weight: 600;
  margin: 0 0 12px;
  color: #333;
  text-align: center;
`

const CardRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${(p) => (p.$isMobile ? '20px 12px' : '24px 20px')};
  width: 100%;
`

const CatalogCard = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

const cardThumbBase = (p) => `
  width: 100%;
  border-radius: 4px;
  overflow: hidden;
  height: auto;
  aspect-ratio: ${p.$isMobile ? '3 / 4' : 'auto'};
`

const CardThumbImg = styled.img`
  ${(p) => cardThumbBase(p)}
  object-fit: cover;
  display: block;
`

const CardThumbBox = styled.div`
  ${(p) => cardThumbBase(p)}
  background: linear-gradient(145deg, #2a2a2a 0%, #1a1a1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
`

const ThumbTitle = styled.span`
  color: #ccc;
  font-size: 14px;
  padding: 16px;
  text-align: center;
  word-break: keep-all;
`

const DownloadBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 12px;
  padding: 12px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: #fff;
  color: #333;
  font-size: 14px;
  text-decoration: none;
  font-family: 'Noto Sans KR', sans-serif;

  &:hover {
    border-color: #999;
  }
`

const DownloadIcon = styled.svg`
  width: 18px;
  height: 18px;
`

const FormWrap = styled.div`
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: ${(p) => (p.$isMobile ? '24px 16px' : '40px 48px')};
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
`

const FormBody = styled.div`
  position: relative;
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  overflow: hidden;
  border-radius: 2px;
`

const FieldRow = styled.div`
  display: flex;
  flex-direction: ${(p) => (p.$isMobile ? 'column' : 'row')};
  align-items: ${(p) =>
    p.$isMobile ? 'stretch' : p.$brand ? 'flex-start' : 'center'};
  margin-bottom: ${(p) => (p.$isMobile ? '20px' : '24px')};
  gap: ${(p) => (p.$isMobile ? '8px' : '12px')};
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
`

const FieldLabel = styled.span`
  flex: 0 0 ${(p) => (p.$isMobile ? 'auto' : '88px')};
  font-size: ${(p) => (p.$isMobile ? '14px' : '15px')};
  font-weight: 500;
  color: #333;
  line-height: 1.4;
`

const TextInput = styled.input`
  flex: 1;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 2px;
  font-size: 14px;
  box-sizing: border-box;
`

const BrandRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${(p) => (p.$isMobile ? '10px' : '12px')};
  flex: 1;
  width: 100%;
  min-width: 0;
`

const BrandBtn = styled.button`
  width: 100%;
  min-width: 0;
  padding: 12px 10px;
  box-sizing: border-box;
  word-break: keep-all;
  overflow-wrap: break-word;
  border: 1px solid ${(p) => (p.$active ? '#c45c26' : '#ddd')};
  background: ${(p) => (p.$active ? '#fff5f0' : '#fff')};
  color: ${(p) => (p.$active ? '#c45c26' : p.$paused ? '#999' : '#333')};
  font-size: 15px;
  font-weight: ${(p) => (p.$active ? '600' : '400')};
  cursor: pointer;
  font-family: 'Noto Sans KR', sans-serif;
  opacity: ${(p) => (p.$paused && !p.$active ? 0.75 : 1)};
`

const PhoneRow = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-width: 0;
`

const PhoneInput = styled.input`
  flex: 1;
  min-width: 0;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 2px;
  text-align: center;
  font-size: 14px;
  box-sizing: border-box;
`

const PhoneDash = styled.span`
  flex-shrink: 0;
  color: #999;
`

const AddressBlock = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  box-sizing: border-box;
`

const AddressRow = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  min-width: 0;
`

const PostcodeBtn = styled.button`
  flex-shrink: 0;
  padding: 10px 16px;
  border: 1px solid #333;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
`

const PrivacyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin: 24px 0 12px;
  font-size: ${(p) => (p.$isMobile ? '13px' : '14px')};
  color: #111;

  label {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
  }

  input[type='checkbox'] {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }
`

const PrivacyLink = styled.a`
  color: #111;
  font-size: ${(p) => (p.$isMobile ? '13px' : '14px')};
  text-decoration: underline;
  flex-shrink: 0;
`

const PrivacyDesc = styled.p`
  margin: 0 0 20px;
  font-size: ${(p) => (p.$isMobile ? '12px' : '13px')};
  color: #333;
  line-height: 1.7;
  word-break: keep-all;
`

const FormDivider = styled.hr`
  margin: 0 0 16px;
  border: none;
  border-top: 1px solid #e0e0e0;
`

const FormNotice = styled.p`
  margin: 0 0 24px;
  font-size: ${(p) => (p.$isMobile ? '12px' : '13px')};
  color: #333;
  line-height: 1.7;
  word-break: keep-all;
`

const SubmitBtn = styled.button`
  width: 100%;
  padding: ${(p) => (p.$isMobile ? '16px' : '18px')};
  background: #e07030;
  color: #fff;
  border: none;
  font-size: ${(p) => (p.$isMobile ? '16px' : '18px')};
  font-weight: 600;
  cursor: pointer;
  font-family: 'Noto Sans KR', sans-serif;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`

const SubmitMsg = styled.p`
  margin-top: 16px;
  text-align: center;
  font-size: 14px;
  color: ${(p) => (p.$type === 'success' ? '#2e7d32' : '#c62828')};
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  background: rgba(70, 70, 70, 0.82);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px 24px;
  border-radius: 4px;
`

const OverlayClose = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
`

const OverlayIcon = styled.div`
  width: ${(p) => (p.$isMobile ? '52px' : '56px')};
  height: ${(p) => (p.$isMobile ? '52px' : '56px')};
  border-radius: 50%;
  background: #fff;
  color: #111;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
`

const WarningTriangle = styled.svg`
  width: 28px;
  height: 28px;
`

const OverlayText = styled.p`
  color: #fff;
  text-align: center;
  font-size: ${(p) => (p.$isMobile ? '14px' : '16px')};
  line-height: 1.6;
  margin: 0;
`

const EmptyHint = styled.p`
  text-align: center;
  color: #999;
  padding: 40px 0;
`
